import React, { useState, useEffect, useRef } from "react";
import "./style.css";
import Loader from "../../components/Loader";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { get, post, del } from "../../services/ApiService";
import { useParams } from "react-router-dom";

const Questionnaire = () => {
  const { id } = useParams();
  const [questions, setQuestions] = useState([]);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isAssessmentCompleted, setIsAssessmentCompleted] = useState(false);
  const [isNotFound, setNotFound] = useState(false);
  const nextQuestionRef = useRef(null);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [tracker, setTracker] = useState([]);
  const [warnings, setWarnings] = useState({});
  const [hasWarnings, setHasWarnings] = useState(false);
  const [hasGivenConsent, setHasGivenConsent] = useState(true);
  const [consent, setConsent] = useState(false);
  const consentRef = useRef(null);
  const [idCheckRequired, setIdCheckRequired] = useState(false);
  const [customerDob, setCustomerDob] = useState('');
  const [dobError, setDobError] = useState('');
  // Date of birth entered as three separate mobile-friendly fields.
  const [dobDay, setDobDay] = useState('');
  const [dobMonth, setDobMonth] = useState('');
  const [dobYear, setDobYear] = useState('');
  const dobDayRef = useRef(null);
  const dobMonthRef = useRef(null);
  const dobYearRef = useRef(null);
  const [idCheckFailed, setIdCheckFailed] = useState(false);
  const [photoIdFile, setPhotoIdFile] = useState(null);
  const [photoIdError, setPhotoIdError] = useState('');
  // True when the assessment is already saved and we only need a photo ID
  // (e.g. the customer reloaded the page after a failed identity check).
  const [photoIdOnly, setPhotoIdOnly] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, [id]);

  useEffect(() => {
    if (nextQuestionRef.current) {
      nextQuestionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [answeredQuestions]);

  // Derive the canonical YYYY-MM-DD value from the Day/Month/Year fields.
  // customerDob is set only when the three fields form a real calendar date;
  // otherwise it is cleared so the "required" validation still fires.
  useEffect(() => {
    const d = parseInt(dobDay, 10);
    const m = parseInt(dobMonth, 10);
    const y = parseInt(dobYear, 10);

    // Nothing entered yet — clear silently, don't nag.
    if (!dobDay && !dobMonth && !dobYear) {
      setCustomerDob('');
      return;
    }

    const complete = dobDay && dobMonth && dobYear.length === 4;
    const inRange =
      d >= 1 && d <= 31 &&
      m >= 1 && m <= 12 &&
      y >= 1900 && y <= new Date().getFullYear();

    if (complete && inRange) {
      // Confirm it's a real date (e.g. reject 31/02).
      const dt = new Date(y, m - 1, d);
      const real =
        dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
      if (real) {
        const iso = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        setCustomerDob(iso);
        setDobError('');
        return;
      }
    }

    // Partially filled or invalid.
    setCustomerDob('');
    if (complete) {
      setDobError('Please enter a valid date of birth.');
    }
  }, [dobDay, dobMonth, dobYear]);

  const handleDobChange = (part, value) => {
    const digits = value.replace(/\D/g, '');
    if (part === 'day') {
      const v = digits.slice(0, 2);
      setDobDay(v);
      if (v.length === 2) dobMonthRef.current?.focus();
    } else if (part === 'month') {
      const v = digits.slice(0, 2);
      setDobMonth(v);
      if (v.length === 2) dobYearRef.current?.focus();
    } else if (part === 'year') {
      setDobYear(digits.slice(0, 4));
    }
    if (dobError) setDobError('');
  };

  const handleDobKeyDown = (part, e) => {
    // Backspace on an empty field jumps to the previous field.
    if (e.key === 'Backspace' && e.target.value === '') {
      if (part === 'month') dobDayRef.current?.focus();
      if (part === 'year') dobMonthRef.current?.focus();
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const res = await get(`api/assessment/${id}`);
      if (res.success) {
        setQuestions(res.questions || []);
        setIdCheckRequired(res.idCheckRequired || false);
        // Reload after a failed Hub check — the questionnaire is already saved,
        // only the photo ID is outstanding. Render just the photo ID section.
        if (res.onlyPhotoId || res.photoIdRequired) {
          setPhotoIdOnly(true);
          setIdCheckRequired(true);
          setIdCheckFailed(true);
        }
        if (res.customerDob) {
          const iso = new Date(res.customerDob).toISOString().split('T')[0]; // YYYY-MM-DD
          const [yy, mm, dd] = iso.split('-');
          setDobYear(yy);
          setDobMonth(mm);
          setDobDay(dd);
          // customerDob is derived from these by the effect above.
        }
        setLoading(false);
        setNotFound(false);
      } else {
        setQuestions([]);
        setLoading(false);
        setNotFound(true);
        if (res.isComplete) {
          setIsAssessmentCompleted(true);
        }
      }
    } catch (error) {
      setQuestions([]);
      setLoading(false);
      setNotFound(true);
    }
  };

  const handleAnswer = (questionId, selectedAnswer) => {
    setEditingQuestionId(null);
    const answeredQuestion = questions.find(
      (question) => question._id === questionId
    );
    const timestamp = new Date().toISOString();

    // Track changes in the tracker state
    setTracker((prev) => {
      // const existingTrackerIndex = prev.findIndex(t => t._id === questionId);
      const updatedTrackerEntry = { _id: questionId, selectedAnswer, timestamp };
      return [...prev, updatedTrackerEntry];
    });

    if (answeredQuestion) {
      const updatedAnsweredQuestion = { ...answeredQuestion, selectedAnswer };
      setAnsweredQuestions((prev) => {
        const existingAnswerIndex = prev.findIndex(q => q._id === questionId);
        if (existingAnswerIndex > -1) {
          const updatedAnswers = [...prev];
          updatedAnswers[existingAnswerIndex] = updatedAnsweredQuestion;
          return updatedAnswers;
        } else {
          return [...prev, updatedAnsweredQuestion];
        }
      });
      const selectOption = selectedAnswer;
      const questionCorrectAnswer = answeredQuestion.correctAnswer;
      const questionWarning = answeredQuestion.warning;
      if (questionCorrectAnswer && questionWarning && questionWarning !== '') {
        if (questionCorrectAnswer === selectOption) {
          setWarnings((prev) => {
            const updatedWarnings = { ...prev };
            delete updatedWarnings[questionId];
            return updatedWarnings;
          });
        } else {
          setWarnings((prev) => ({ ...prev, [questionId]: questionWarning }));
          setEditingQuestionId(answeredQuestion._id);
        }
      }
      const newProgress =
        ((answeredQuestions.length + 1) / questions.length) * 100;
      setProgressPercentage(newProgress);
    }
    setHasWarnings(Object.keys(warnings).length > 0);
  };

  const getNextQuestionId = () => {
    const answeredIds = answeredQuestions.map((question) => question._id);
    const nextQuestion = questions.find(
      (question) => !answeredIds.includes(question._id)
    );
    return nextQuestion ? nextQuestion._id : null;
  };

  const renderQuestion = (questionId) => {
    const question = questions.find((question) => question._id === questionId);
    if (!question) {
      return null; // Return null if no more questions
    }
    const warningMessage = warnings[questionId];
    return (
      <div className="col-lg-12 col-md-12 col-sm-12 col-12" key={question._id} id={`question-${question._id}`} 
        ref={question._id === getNextQuestionId() ? nextQuestionRef : null}
      >
        <div className="d-flex flex-column flex-sm-row gap-3">
          <img
            src="/assets/images/chat/user.jpeg"
            className="img-fluid rounded-3"
            width={70}
            style={{ height: '45px', marginTop: '10px' }}
            alt=""
          />
          <div className="w-100">
            <label className="pb-1 fs-16 text-black m-3">
              {question.question}
            </label>
            {question.description !== '' &&
              <div className="question_description"
                dangerouslySetInnerHTML={{ __html: question.description }}
              />
            }
            {renderInput(question)}
            {warningMessage && (
              <div className="alert alert-danger question-warning" role="alert">
                {warningMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderInput = (question) => {
    const currentAnswer = answeredQuestions.find(q => q._id === question._id)?.selectedAnswer || '';
    switch (question.type) {
      case "select":
        return (
          <select
            className="form-select bordr-color-main rounded-5 fs-14 pad-14-20 w-300"
            value={currentAnswer}
            onChange={(e) => handleAnswer(question._id, e.target.value)}
          >
            <option value="" disabled>Please Select</option>
            {question.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      case "radio":
        return (
          <div className="d-flex align-items-center gap-2">
            {question.options.map((option) => (
              <div
                key={option}
                className="form-check bordr-color-main border pad-14-20 rounded-5"
              >
                <input
                  className="form-check-input colorchange ms-0"
                  type="radio"
                  name={question._id}
                  id={option}
                  value={option}
                  checked={currentAnswer === option}
                  onClick={(e) => handleAnswer(question._id, e.target.value)}
                />
                <label className="form-check-label fs-14 ps-2" htmlFor={option}>
                  {option}
                </label>
              </div>
            ))}
          </div>
        );
      case "textarea":
        return (
          <textarea
            rows={3}
            cols={3}
            type="text"
            className="form-control fs-14 resize-none border-0 bg-transparent"
            placeholder="Write here..."
            defaultValue={currentAnswer}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault(); // Prevents the default behavior (like line break in textarea)
                handleAnswer(question._id, e.target.value);
              }
            }}
            onBlur={(e) => handleAnswer(question._id, e.target.value)} // Handle blur event to mark answer
          />
        );
      default:
        return null;
    }
  };


  const handleSubmit = async (e) => {

    e.preventDefault();

    // Photo-ID-only resubmission (page was reloaded after a failed ID check).
    // The questionnaire answers are already saved server-side, so we skip the
    // consent / DOB / question validations and only need the photo ID.
    if (photoIdOnly) {
      if (!photoIdFile) {
        setPhotoIdError('Please upload a photo of your ID to continue.');
        return;
      }
      try {
        setIsSubmit(true);
        setPhotoIdError('');
        const formData = new FormData();
        formData.append('submitWithPhotoId', 'true');
        formData.append('photoId', photoIdFile);
        if (customerDob) formData.append('customerDob', customerDob);
        const res = await post(`api/assessment/taking/${id}`, formData, true);
        if (res.success) {
          setIsSubmit(false);
          setIsComplete(true);
        } else {
          setIsSubmit(false);
          alert(res.error || 'Something went wrong. Please try again.');
        }
      } catch (error) {
        setIsSubmit(false);
        const message = error?.response?.data?.error || 'Something went wrong. Please try again.';
        alert(message);
      }
      return;
    }

    if (!consent) {
      setHasGivenConsent(false);
      if (consentRef.current) {
        consentRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        consentRef.current.focus();
      }

      return;
    }

    if (idCheckRequired && !customerDob) {
      // If they typed something but it isn't a valid date, say so; otherwise
      // it's simply missing.
      const anyEntered = dobDay || dobMonth || dobYear;
      setDobError(anyEntered
        ? 'Please enter a valid date of birth.'
        : 'Please provide your date of birth to continue.');
      document.getElementById('customerDob')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (idCheckRequired && customerDob >= new Date().toISOString().split('T')[0]) {
      setDobError('Date of birth cannot be today or a future date.');
      document.getElementById('customerDob')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Check if all questions are answered
    if (answeredQuestions.length !== questions.length) {
      alert("Please answer all questions before submitting.");
      return;
    }
    // Check for warnings - this is the critical fix
    const currentWarnings = { ...warnings };
    // Double-check all answers against correct answers
    questions.forEach(question => {
      const answer = answeredQuestions.find(q => q._id === question._id);
      if (question.correctAnswer && question.warning && answer) {
        if (question.correctAnswer !== answer.selectedAnswer) {
          currentWarnings[question._id] = question.warning;
        }
      }
    });
    
    // If there are any warnings, prevent submission
    if (Object.keys(currentWarnings).length > 0) {
      // Scroll to the first question with a warning
      const firstWarningId = Object.keys(currentWarnings)[0];
      setWarnings(currentWarnings);
      setHasWarnings(true);
      
      // Find and scroll to the question with warning
      const warningElement = document.getElementById(`question-${firstWarningId}`);
      if (warningElement) {
        warningElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      
      alert("Please correct the answers that triggered warnings before submitting.");
      return;
    }


    if (idCheckFailed && !photoIdFile) {
      setPhotoIdError('Please upload a photo of your ID to continue.');
      return;
    }

    try {
      setIsSubmit(true);
      setPhotoIdError('');

      let res;
      if (idCheckFailed) {
        const formData = new FormData();
        formData.append('customerResponses', JSON.stringify(answeredQuestions));
        formData.append('tracker', JSON.stringify(tracker));
        formData.append('submitWithPhotoId', 'true');
        if (customerDob) formData.append('customerDob', customerDob);
        formData.append('photoId', photoIdFile);
        res = await post(`api/assessment/taking/${id}`, formData, true);
      } else {
        const Body = {
          customerResponses: answeredQuestions,
          tracker,
          ...(idCheckRequired && customerDob ? { customerDob } : {}),
        };
        res = await post(`api/assessment/taking/${id}`, Body);
      }

      if (res.success) {
        setIsSubmit(false);
        setIsComplete(true);
      } else {
        setIsSubmit(false);
        if (res.idCheckFailed) {
          setIdCheckFailed(true);
        } else if (res.error) {
          alert(res.error);
        }
      }
    } catch (error) {
      setIsSubmit(false);
      const message = error?.response?.data?.error || 'Something went wrong. Please try again.';
      alert(message);
    }
  };

  const handlePhotoIdChange = (e) => {
    setPhotoIdFile(e.target.files?.[0] || null);
    setPhotoIdError('');
  };

  const handleConsentChange = (e) => {
    const isChecked = e.target.checked;
    setConsent(isChecked);
    setHasGivenConsent(isChecked);
  };



  return (
    <>
      <div>
        <header className="border-bottom fixed-top bg-white header-section">
          <div className="container">
            <div className="py-3">
              <img
                src="/assets/images/logo.svg"
                className="img-fluid invert_logo"
                width="150px"
                alt=""
              />
            </div>
          </div>
        </header>
        <section className="py-5">
          <div className="container">
            <div className="row justify-content-center main-section">
              {loading && <Loader />}
              {!loading && !isNotFound && !isComplete && (
                <>
                  <div className="col-lg-10">
                    <div className="card rounded-3">
                      <div className="p-4">
                        <div className="row g-3 align-items-center">
                          <div className="col-lg-7">
                            <div>
                              <h3 className="main-heading">
                                {photoIdOnly
                                  ? "Almost there — we just need a photo of your ID to complete your identity check."
                                  : "Just a few questions from our Pharmacist before you checkout to make sure this medicine is safe for you."}
                              </h3>
                              <div className="d-flex align-items-center">
                                <div className="line" />
                                <div className="smdot" />
                                <div className="smdot" />
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-5 text-center justify-content-center">
                            <CircularProgressbar
                              value={
                                questions.length
                                  ? (answeredQuestions.length / questions.length) * 100
                                  : 100
                              }
                              text={`${Math.round(
                                questions.length
                                  ? (answeredQuestions.length / questions.length) * 100
                                  : 100
                              )}%`}
                              strokeWidth={8}
                              styles={buildStyles({
                                textSize: "14px",
                                pathColor: `#4caf50`,
                                textColor: "#4caf50",
                              })}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="card-header justify-content-between d-flex align-items-center">
                        <div className="d-flex align-items-center gap-3">
                          <img
                            src="/assets/images/chat/user.jpeg"
                            className="img-fluid rounded-3"
                            width={70}
                            style={{ height: '45px' }}
                            alt="user-img"
                          />
                          <h6 className="m-0 fw-semibold">GPhC No. 2216145</h6>
                        </div>
                        <div>
                          <img
                            src="/assets/images/chat/registered-pharmacy1.png"
                            className="img-fluid"
                            style={{ width: '80px' }}
                            alt="user-img"
                          />
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-12 col-md-12 col-sm-12 col-12">
                            {answeredQuestions.map((answeredQuestion) => (
                              <>
                                {editingQuestionId === answeredQuestion._id ? (
                                  renderQuestion(answeredQuestion._id)
                                ) : (
                                  <>
                                    <div className="col-lg-12">
                                      <div className="d-flex align-items-center gap-3">
                                        <img
                                          src="/assets/images/chat/user.jpeg"
                                          className="img-fluid rounded-3"
                                          width={70}
                                          style={{ height: '45px' }}
                                          alt="user-img"
                                        />
                                        <div className="w-100">
                                          <label className="pb-1 fs-16 text-black m-3">
                                            {answeredQuestion.question}
                                          </label>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-12">
                                      <div className="d-flex align-items-center gap-2 justify-content-end">
                                        <div className="right-block-box text-white fs-14">
                                          {answeredQuestion.selectedAnswer}
                                        </div>
                                        <div className="flex-shrink-0">
                                          <button
                                            type="button"
                                            className="border-0 bg-transparent btn-edit"
                                            title="Edit"
                                            style={{ opacity: '.6', margin: '5px' }}
                                            onClick={() => setEditingQuestionId(answeredQuestion._id)}
                                          >
                                            <span data-bs-toggle="tooltip" data-bs-title="Edit">
                                              <img src="/assets/images/chat/dots.png" className="img-fluid" width="16px" alt="user-img" />
                                            </span>
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </>
                            ))}
                          </div>
                          {editingQuestionId === null && renderQuestion(getNextQuestionId())}
                        </div>
                        {getNextQuestionId() === null && Object.keys(warnings).length === 0 && (
                          <div className="card-footer" style={{ marginTop: '20px', padding: '10px' }}>
                            <div className="completion-section text-center">
                              {/* DOB Section — shown when ID check is required */}
                              {idCheckRequired && (
                                <div className="mb-4 p-3 rounded" style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '2px solid #e9ecef',
                                  textAlign: 'left'
                                }}>
                                  <h6 style={{ color: '#2c3e50', fontWeight: '600', fontSize: '16px', marginBottom: '8px' }}>
                                    <i className="fas fa-id-card me-2" style={{ color: '#4CAF50' }}></i>
                                    Identity Verification Required
                                  </h6>
                                  <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '12px' }}>
                                    Age verification is required for this order due to regulations covering one of the medication’s ingredients. Please enter your date of birth.
                                  </p>
                                  <label style={{ fontSize: '14px', fontWeight: '500', display: 'block', marginBottom: '2px' }}>
                                    Date of Birth <span style={{ color: 'red' }}>*</span>
                                  </label>
                                  <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px' }}>
                                    For example, 14 03 1988
                                  </div>
                                  <div id="customerDob" className="dob-fields" style={{ display: 'flex', gap: '10px', flexWrap: 'nowrap' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '68px' }}>
                                      <label htmlFor="dobDay" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Day</label>
                                      <input
                                        id="dobDay"
                                        ref={dobDayRef}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        autoComplete="bday-day"
                                        placeholder="DD"
                                        maxLength={2}
                                        className="form-control text-center"
                                        style={{ fontSize: '20px', padding: '12px 6px', borderColor: dobError ? '#dc3545' : undefined, borderWidth: '2px' }}
                                        value={dobDay}
                                        onChange={(e) => handleDobChange('day', e.target.value)}
                                        onKeyDown={(e) => handleDobKeyDown('day', e)}
                                      />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '68px' }}>
                                      <label htmlFor="dobMonth" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Month</label>
                                      <input
                                        id="dobMonth"
                                        ref={dobMonthRef}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        autoComplete="bday-month"
                                        placeholder="MM"
                                        maxLength={2}
                                        className="form-control text-center"
                                        style={{ fontSize: '20px', padding: '12px 6px', borderColor: dobError ? '#dc3545' : undefined, borderWidth: '2px' }}
                                        value={dobMonth}
                                        onChange={(e) => handleDobChange('month', e.target.value)}
                                        onKeyDown={(e) => handleDobKeyDown('month', e)}
                                      />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '96px' }}>
                                      <label htmlFor="dobYear" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Year</label>
                                      <input
                                        id="dobYear"
                                        ref={dobYearRef}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        autoComplete="bday-year"
                                        placeholder="YYYY"
                                        maxLength={4}
                                        className="form-control text-center"
                                        style={{ fontSize: '20px', padding: '12px 6px', borderColor: dobError ? '#dc3545' : undefined, borderWidth: '2px' }}
                                        value={dobYear}
                                        onChange={(e) => handleDobChange('year', e.target.value)}
                                        onKeyDown={(e) => handleDobKeyDown('year', e)}
                                      />
                                    </div>
                                  </div>
                                  {dobError && (
                                    <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '6px' }}>
                                      {dobError}
                                    </div>
                                  )}

                                  {idCheckFailed && (
                                    <div
                                      className="mt-4 p-3 rounded"
                                      style={{
                                        backgroundColor: '#fff3cd',
                                        border: '2px solid #ffc107',
                                      }}
                                    >
                                      <h6 style={{ color: '#856404', fontWeight: '600', fontSize: '15px', marginBottom: '8px' }}>
                                        <i className="fas fa-exclamation-circle me-2"></i>
                                        Photo ID Required
                                      </h6>
                                      <p style={{ fontSize: '14px', color: '#664d03', marginBottom: '12px' }}>
                                        We were unable to verify your identity automatically. <br/>Please upload a clear photo
                                        of a valid photo ID (passport or driving licence) and submit again.
                                      </p>
                                      <label htmlFor="photoIdUpload" style={{ fontSize: '14px', fontWeight: '500', display: 'block', marginBottom: '4px' }}>
                                        Upload Photo ID <span style={{ color: 'red' }}>*</span>
                                      </label>
                                      <input
                                        type="file"
                                        id="photoIdUpload"
                                        className="form-control w-300"
                                        accept="image/jpeg,image/png,image/webp,application/pdf"
                                        style={{ borderColor: photoIdError ? 'red' : undefined }}
                                        onChange={handlePhotoIdChange}
                                      />
                                      {photoIdError && (
                                        <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px' }}>
                                          {photoIdError}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              {/* Consent Section - Enhanced (hidden on photo-ID-only resubmission) */}
                              {!photoIdOnly && (
                              <div className="consent-section mb-4 p-3 rounded" style={{
                                backgroundColor: '#f8f9fa',
                                border: '2px solid #e9ecef',
                                textAlign: 'left'
                              }}
                              >
                                <div className="consent-header mb-3">
                                  <h6 style={{
                                    color: '#2c3e50',
                                    fontWeight: '600',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}>
                                    <i className="fas fa-shield-alt" style={{ color: '#4CAF50' }}></i>
                                    Consent & Confirmation Required
                                  </h6>
                                </div>
                                {!hasGivenConsent && (
                                  <div className="validation-message mt-3" style={{
                                    color: '#dc3545',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'left',
                                    justifyContent: 'left',
                                    gap: '6px'
                                  }}>
                                    {/* <i className="fas fa-exclamation-circle"></i> */}
                                    Please agree to the consent statement to submit your assessment
                                  </div>
                                )}
                                <div className="form-check d-flex align-items-start" ref={consentRef}>
                                  <input
                                    className="form-check-input me-2 flex-shrink-0"
                                    type="checkbox"
                                    id="consentCheckbox"
                                    checked={consent}
                                    onChange={handleConsentChange}
                                    style={{
                                      width: "25px",
                                      height: "25px",
                                      marginTop: "2px",
                                      borderColor: hasGivenConsent ? "black" : "red",
                                    }}
                                  />

                                  <label
                                    className="form-check-label flex-grow-1"
                                    htmlFor="consentCheckbox"
                                    style={{
                                      fontSize: "14px",
                                      color: "#1a1a1a",
                                      lineHeight: "1.6",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <ul style={{ margin: "0", paddingLeft: "20px" }}>
                                      <li style={{ marginBottom: "6px" }}>
                                        I confirm my answers regarding my condition are accurate.
                                      </li>
                                      <li style={{ marginBottom: "6px" }}>
                                        I understand that this specific online supply requires clinical approval, and a registered pharmacist must assess my details against pharmacy clinical criteria to confirm this medication is appropriate for my condition before they can authorise and dispense it. If the supply is not approved, my order will be cancelled and refunded in full.
                                      </li>
                                      <li>
                                        I consent to the processing of my personal and health information for this medication questionnaire. Your data will be processed &amp; recorded securely and in full compliance with GDPR and Pharmacy Clinical framework.
                                      </li>
                                    </ul>
                                  </label>
                                </div>
                              </div>
                              )}

                              {/* Submit Button Section */}
                              <div className="submit-section">
                                <button
                                  className="btn btn-primary btn-lg"
                                  onClick={handleSubmit}
                                  disabled={isSubmit || Object.keys(warnings).length > 0}
                                  style={{
                                    padding: '12px 40px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    borderRadius: '8px',
                                    backgroundColor: '#4e7661',
                                    borderColor: '#4e7661',
                                    opacity: 1,
                                    transition: 'all 0.3s ease',
                                    minWidth: '180px'
                                  }}
                                // onMouseEnter={(e) => {
                                //   if (hasGivenConsent) {
                                //     e.target.style.transform = 'translateY(-2px)';
                                //     e.target.style.boxShadow = '0 4px 12px rgba(76, 175, 80, 0.3)';
                                //   }
                                // }}
                                // onMouseLeave={(e) => {
                                //   if (hasGivenConsent) {
                                //     e.target.style.transform = 'translateY(0)';
                                //     e.target.style.boxShadow = 'none';
                                //   }
                                // }}
                                >
                                  {isSubmit ? (
                                    <>
                                      <i className="fa fa-spinner fa-spin me-2"></i>
                                      Submitting...
                                    </>
                                  ) : idCheckFailed ? (
                                    <>
                                      <i className="fas fa-upload me-2"></i>
                                      Submit with Photo ID
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-paper-plane me-2"></i>
                                      Submit Assessment
                                    </>
                                  )}
                                </button>

                                {/* Validation Message */}
                                {Object.keys(warnings).length > 0 && (
                                  <div className="alert alert-warning mt-3" role="alert">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    Please correct the answers before submitting.
                                  </div>
                                )}

                                {/* Additional Info */}
                                <div className="additional-info mt-4" style={{
                                  fontSize: '12px',
                                  color: '#6c757d',
                                  fontStyle: 'italic'
                                }}>
                                  <p className="mb-1">
                                    <i className="fas fa-clock me-1"></i>
                                    Our team typically reviews assessments within 2-4 hours
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                      </div>

                    </div>
                  </div>
                </>
              )}

              {isNotFound && !isAssessmentCompleted && (
                <div class="col-12 text-center mb-5">
                  <div class="error-template">
                    <h1> Oops!</h1>
                    <h2>404 Not Found</h2>
                    <div class="error-details">
                      Sorry, an error has occured, Requested page not found!
                    </div>
                  </div>
                </div>
              )}
              {isNotFound && isAssessmentCompleted && (
                <div class="col-12 text-center mb-5">
                  <div className="card card-thanks" style={{ backgroundColor: 'aliceblue' }}>
                    <div className="card-body" >
                      <div class="error-template" style={{ padding: '30px' }}>
                        <h4 className="font-theme">This assessment has been completed</h4>
                        <div class="error-details font-theme">
                          If you believe you still need to complete an assessment, please get in touch with us
                          <a href="https://www.ebay.co.uk/cnt/intermediatedFAQ?requested=pillpharm"> via eBay message</a>
                        </div>
                        <a className="btn btn-outline-primary btn-sm mt-4 font-theme" href="https://www.ebay.co.uk/str/pillpharm">Retun to eBay</a>
                      </div>
                    </div>
                  </div>
                </div>
              )}


              {isComplete && (
                <div className="col-12 text-center p-5">
                  <div className="card card-thanks" style={{ backgroundColor: 'aliceblue' }}>
                    <div className="card-body">
                      <img src={`/assets/images/thank-you.png`} style={{ width: '100px' }} alt="" />
                      <h6 className="success-title font-theme">Thank you for completing the assessment</h6>
                      <p className="m-0 p-0 success-dec font-theme">Our pharmacy team will review your answers.</p>
                      <p className="m-0 p-0 success-dec font-theme">If approved, you will receive confirmation of dispatch.</p>
                      <p className="m-0 p-0 success-dec font-theme">You may now close this tab.</p>
                      <a className="btn btn-outline-primary btn-sm mt-4 font-theme" href="https://www.ebay.co.uk/str/pillpharm">Retun to eBay</a>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Questionnaire;
